import { useState } from 'react'

import isEmpty from 'lodash-es/isEmpty'

import { Box } from 'app/components/design-system-components'
import { SearchRounded } from 'app/components/design-system-components/icons/actions'
import { Input } from 'app/components/design-system-components/inputs'
import { CheckboxesList } from 'app/components/design-system-components/list-filters/CheckboxesList'
import { DropdownBodySectionHeader } from 'app/components/design-system-components/list-filters/DropdownBodySectionHeader'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { isMobile } from 'app/shared_components/helpers'
import {
  applicationWorkflowAcceptStages,
  applicationWorkflowApproveStages,
  applicationWorkflowGeneralStages,
  applicationWorkflowOfferStages,
  applicationWorkflowReviewStages,
  workflowsOptions,
} from 'app/sm/manager_viewings/ManagerViewingsFilters/constants'

const applicationWorkflowFilterFields = {
  workflow: 'workflow',
}

export const applicationWorkflowFilterDataExtractor = (filterValues: {
  [x: string]: {} | null
}) => {
  const { [applicationWorkflowFilterFields.workflow]: workflowObj } =
    filterValues
  return {
    workflow: Object.values(workflowObj || {}).map(({ option }: any) => option),
  }
}

export const ApplicationWorkflowAppliedFilterRenderer = ({
  filterValues,
}: any) => {
  const { [applicationWorkflowFilterFields.workflow]: workflowObj } =
    filterValues
  return Object.values(workflowObj)
    .map((workflowOption: any) => workflowOption.optionMeta.displayText)
    .join(', ')
}

export const isApplicationWorkflowApplied = (
  applicationWorkflowFilterValues: any,
) => {
  return (
    applicationWorkflowFilterValues &&
    applicationWorkflowFilterValues[applicationWorkflowFilterFields.workflow] &&
    Object.values(
      applicationWorkflowFilterValues[applicationWorkflowFilterFields.workflow],
    ).length
  )
}

export const getWorkflowFilterState = (workflow: any) =>
  isMobile() && !isEmpty(workflow)
    ? [+workflow]
    : Object.values(workflow?.workflow || {}).map(
        ({ optionMeta }: any) => optionMeta?.value,
      )

const ApplicationWorkflowOptionsList = ({
  listTitle,
  options,
  selectedOptions,
  onCheckBoxClicked,
}: any) => {
  if (!options || !options.length) return null
  return (
    <>
      {listTitle && (
        <DropdownBodySectionHeader
          header={listTitle}
          containerProps={{ mb: theme.space[4] + 'px' }}
        />
      )}

      <CheckboxesList
        options={options}
        onCheckBoxClicked={onCheckBoxClicked}
        checkedOptions={selectedOptions}
      />
    </>
  )
}

export const ApplicationWorkflowFilterBody = ({
  formBag: { values, setFieldValue },
}: any) => {
  const [searchText, setSearchText] = useState('')

  const onSearchTextChange = ({ target: { value } }: any) => {
    setSearchText(value)
  }

  const options = workflowsOptions.filter(({ optionMeta: { displayText } }) =>
    displayText?.toLowerCase().includes(searchText.toLowerCase()),
  )

  const onCheckBoxClicked = (
    checked: any,
    option: { optionMeta: { id: any } },
  ) => {
    const propertyValues = values[applicationWorkflowFilterFields.workflow]

    if (checked) {
      setFieldValue(applicationWorkflowFilterFields.workflow, {
        [option.optionMeta.id]: option,
      })
    } else {
      const { [option.optionMeta.id]: uncheckedOption, ...otherOptions } =
        propertyValues
      setFieldValue(applicationWorkflowFilterFields.workflow, {
        ...otherOptions,
      })
    }
  }

  const renderOptions = () => {
    const selectedWorkflows =
      values[applicationWorkflowFilterFields.workflow] || {}

    const clonedOptions = [...options]
    const generalOptions = clonedOptions.filter((option) =>
      Object.keys(applicationWorkflowGeneralStages).includes(
        option.optionMeta.id,
      ),
    )
    const reviewOptions = clonedOptions.filter((option) =>
      Object.keys(applicationWorkflowReviewStages).includes(
        option.optionMeta.id,
      ),
    )
    const approveOptions = clonedOptions.filter((option) =>
      Object.keys(applicationWorkflowApproveStages).includes(
        option.optionMeta.id,
      ),
    )
    const offerOptions = clonedOptions.filter((option) =>
      Object.keys(applicationWorkflowOfferStages).includes(
        option.optionMeta.id,
      ),
    )
    const acceptOptions = clonedOptions.filter((option) =>
      Object.keys(applicationWorkflowAcceptStages).includes(
        option.optionMeta.id,
      ),
    )

    return (
      <Box maxHeight="300px" overflowY="auto">
        <ApplicationWorkflowOptionsList
          options={generalOptions}
          listTitle=""
          onCheckBoxClicked={onCheckBoxClicked}
          selectedOptions={selectedWorkflows}
        />
        <ApplicationWorkflowOptionsList
          options={reviewOptions}
          listTitle="Review"
          onCheckBoxClicked={onCheckBoxClicked}
          selectedOptions={selectedWorkflows}
        />
        <ApplicationWorkflowOptionsList
          options={approveOptions}
          listTitle="Approve"
          onCheckBoxClicked={onCheckBoxClicked}
          selectedOptions={selectedWorkflows}
        />
        <ApplicationWorkflowOptionsList
          options={offerOptions}
          listTitle="Offer"
          onCheckBoxClicked={onCheckBoxClicked}
          selectedOptions={selectedWorkflows}
        />
        <ApplicationWorkflowOptionsList
          options={acceptOptions}
          listTitle="Accept"
          onCheckBoxClicked={onCheckBoxClicked}
          selectedOptions={selectedWorkflows}
        />
      </Box>
    )
  }

  return (
    <Box maxHeight="100%">
      <Box p={theme.space[4] + 'px'}>
        <Input
          icon={<SearchRounded style={{ color: theme.colors.gray400 }} />}
          inputProps={{
            onChange: onSearchTextChange,
            placeholder: 'Search for a status',
          }}
          id={undefined}
          prefix={undefined}
          postfix={undefined}
          endedIcon={undefined}
          containerProps={undefined}
        />
      </Box>

      {renderOptions()}
    </Box>
  )
}
